'use client';

/**
 * Hook for polling messages from a support thread
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type {
  ChatMessage,
  GetMessagesResponse,
  SupportChatConfig,
  ThreadId,
} from '../types/support-chat';

export function useMessagePolling(
  threadId: ThreadId | null,
  isOpen: boolean,
  config: SupportChatConfig,
  isUserActive: boolean = true,
  bearerToken?: string,
  /**
   * Signed thread grant. REQUIRED by the server — it resolves the thread from
   * this rather than from the id in the URL, because a Slack thread_ts is an
   * enumerable timestamp and scoping on it alone exposed other customers'
   * conversations. Polling is skipped entirely without it, since every request
   * would be refused.
   */
  threadGrant?: string | null
) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastMessageIdRef = useRef<string | null>(null);
  const isPageVisibleRef = useRef<boolean>(true);
  // TYPING_FOCUS_STABILITY: `isLoading` gates disable-able UI. Background polls
  // must NOT flip it — a loading flag wired to an input's `disabled` blurs the
  // user on every poll tick. Only the FIRST load per thread surfaces isLoading.
  const hasLoadedOnceRef = useRef<boolean>(false);

  // A new thread gets one fresh initial-load indicator again.
  useEffect(() => {
    hasLoadedOnceRef.current = false;
  }, [threadId]);

  const fetchMessages = useCallback(async () => {
    // No grant means the server will refuse every request — don't poll a 403.
    if (!threadId || !threadGrant) {
      return;
    }

    const isInitialLoad = !hasLoadedOnceRef.current;

    try {
      if (isInitialLoad) {
        setIsLoading(true);
      }
      setError(null);

      const apiBaseUrl = config.apiBaseUrl || '/api/support';
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (bearerToken) {
        headers['Authorization'] = `Bearer ${bearerToken}`;
      }
      // Sent as a header rather than in the path/query so the credential stays
      // out of access logs.
      headers['x-support-thread-grant'] = threadGrant;

      const response = await fetch(`${apiBaseUrl}/messages/${threadId}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to fetch messages' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data: GetMessagesResponse = await response.json();
      
      // Only update if we have new messages (check before updating to avoid unnecessary re-renders).
      // Updating a SIBLING message list never blurs the composer textarea — the
      // textarea element is reconciled in place, not remounted — so no focus
      // restoration hack is needed here (see TYPING_FOCUS_STABILITY).
      if (data.messages.length > 0) {
        const lastMessage = data.messages[data.messages.length - 1];
        if (lastMessageIdRef.current !== lastMessage.id) {
          setMessages(data.messages);
          lastMessageIdRef.current = lastMessage.id;
        }
      } else {
        // No messages returned. State already initializes to []; return the SAME
        // reference so React bails out of the update. (Returning a fresh [] here
        // would re-render the widget on every empty poll — needless churn while
        // the user is typing.)
        setMessages((prevMessages) => prevMessages);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch messages';
      setError(errorMessage);
      console.error('Error fetching messages:', err);
    } finally {
      if (isInitialLoad) {
        hasLoadedOnceRef.current = true;
        setIsLoading(false);
      }
    }
  }, [threadId, config, bearerToken, threadGrant]);

  // Handle page visibility changes (stop polling when tab/window is hidden)
  useEffect(() => {
    const handleVisibilityChange = () => {
      const wasVisible = isPageVisibleRef.current;
      isPageVisibleRef.current = !document.hidden;

      // If page becomes hidden, stop polling
      if (document.hidden && pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      // If page becomes visible and chat is open with a thread, resume polling
      else if (!document.hidden && wasVisible === false && isOpen && threadId && !pollingIntervalRef.current) {
        const pollInterval = config.pollInterval || 2500;
        pollingIntervalRef.current = setInterval(() => {
          // Double-check visibility before each poll
          if (isPageVisibleRef.current) {
            fetchMessages();
          }
        }, pollInterval);
        // Fetch immediately when page becomes visible
        fetchMessages();
      }
    };

    // Initialize visibility state
    isPageVisibleRef.current = !document.hidden;

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // This effect's handler can START a poll (on becoming visible again), so
      // it has to be able to stop one. It relied on the sibling polling effect
      // to clear the shared ref — which works only while that effect is also
      // mounted with the same ref, and left the interval running on any
      // unmount path where it is not. Own what you start.
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isOpen, threadId, fetchMessages, config.pollInterval]);

  useEffect(() => {
    if (!threadId || !isOpen) {
      // Stop polling when chat is closed or no thread ID
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Stop polling if user is inactive (hasn't typed for 1 minute)
    if (!isUserActive) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Only start polling if page is visible
    if (!isPageVisibleRef.current) {
      return;
    }

    // Fetch immediately when opened or when user becomes active
    fetchMessages();

    // Set up polling interval
    const pollInterval = config.pollInterval || 2500;
    pollingIntervalRef.current = setInterval(() => {
      // Only poll if page is still visible and user is active
      if (isPageVisibleRef.current && isUserActive) {
        fetchMessages();
      }
    }, pollInterval);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [threadId, isOpen, isUserActive, fetchMessages, config.pollInterval]);

  return {
    messages,
    isLoading,
    error,
    refreshMessages: fetchMessages,
  };
}

